import React from 'react';
import {
  FileText, BarChart3, Brain, History,
  LogOut, ChevronRight, User, type LucideIcon,
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { User as FirebaseUser } from 'firebase/auth';

export type AppView = 'builder' | 'dashboard' | 'commandCenter' | 'history';

interface Props {
  currentView: AppView;
  onNavigate:  (v: AppView) => void;
  user:        FirebaseUser | null;
}

const navItems: { id: AppView; label: string; icon: LucideIcon; badge?: string }[] = [
  { id: 'builder',       label: 'Quote Builder',     icon: FileText,   badge: 'NEW' },
  { id: 'dashboard',     label: 'Comparison',        icon: BarChart3 },
  { id: 'commandCenter', label: 'Command Center',    icon: Brain },
  { id: 'history',       label: 'My Quotes',         icon: History },
];

export const Navigation: React.FC<Props> = ({ currentView, onNavigate, user }) => {
  const handleSignOut = () => signOut(auth);

  return (
    <aside className="w-60 shrink-0 flex flex-col h-screen bg-[#0D0D1A] border-r border-white/5">
      {/* Logo */}
      <div className="p-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0D0D1A] flex items-center justify-center shadow-lg shadow-purple-500/30 overflow-hidden">
            <img src="/nxtkonekt-logo.svg" alt="NxtKonekt" className="w-full h-full" />
          </div>
          <div>
            <div className="font-black text-sm text-white">
              Nxt<span className="text-purple-400">Konekt</span>
            </div>
            <div className="text-[10px] text-gray-600 font-medium tracking-wider uppercase">ProQuote</div>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest px-3 pt-2 pb-1">Navigation</div>
        {navItems.map(({ id, label, icon: Icon, badge }) => {
          const active = currentView === id;
          return (
            <button
              key={id}
              onClick={() => onNavigate(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                active
                  ? 'bg-purple-600/20 text-purple-300 border border-purple-500/20 shadow-sm'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon
                size={17}
                className={active ? 'text-purple-400' : 'text-gray-500 group-hover:text-gray-300'}
              />
              <span className="flex-1 text-left">{label}</span>
              {badge && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-purple-500/20 text-purple-400 border border-purple-500/20">
                  {badge}
                </span>
              )}
              {active && <ChevronRight size={14} className="text-purple-400 opacity-60" />}
            </button>
          );
        })}
      </nav>

      {/* User / Sign out */}
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shrink-0">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <User size={14} className="text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-white truncate">
              {user?.displayName || user?.email?.split('@')[0] || 'Partner'}
            </div>
            <div className="text-[10px] text-gray-600 truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleSignOut}
            title="Sign out"
            className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
};
